/**
 * Кнопка + правая шторка с деревом областей — оболочка вокруг `ScopeTreeView`.
 *
 * Зачем отдельным файлом: разметка «пилюля с именем выбранного → шторка
 * справа» к моменту появления третьего потребителя была написана дважды —
 * в пилюле области шапки (`DashboardScopeSelector`, удалена в AK-895) и в
 * фильтре «Аналитики прошлых лет». Третью копию заводить нельзя (тот же
 * довод, по которому из дашборда вынимали само дерево), поэтому оболочка
 * живёт здесь.
 *
 * Компонент НИЧЕГО не решает про смысл выбора: не ходит в сеть, не зовёт
 * `POST /scope/set`, не трогает глобальную область сессии. Выбор он отдаёт
 * наружу и закрывает шторку. Потребитель, которому нужен глобальный
 * переключатель, дописывает это сам (как `FarmSwitcher` через
 * `useScopeSetter`); все
 * остальные получают локальный фильтр экрана по умолчанию — и это правильный
 * дефолт: человек, заглянувший в справочник соседнего хозяйства, не должен
 * обнаружить, что у него переключилась вся система.
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet';
import { ScopeTreeView, type ScopeSelection, type ScopeTreeViewProps } from './ScopeTree';
import { cn } from '@/lib/utils';

export interface ScopeTreePickerProps extends ScopeTreeViewProps {
  /** Что написано на кнопке — имя того, что выбрано сейчас. */
  triggerLabel: string;
  /** Иконка на кнопке: по ней видно, какого рода уровень выбран. */
  triggerIcon?: ReactNode;
  /** Приписка справа на кнопке — гектары или короткое пояснение. */
  triggerHint?: string;
  /** Заголовок шторки для скринридера. */
  sheetTitle: string;
  /** Пояснение шторки для скринридера — в том числе «выбор локальный». */
  sheetDescription: string;
  disabled?: boolean;
  className?: string;
}

export function ScopeTreePicker({
  triggerLabel,
  triggerIcon,
  triggerHint,
  sheetTitle,
  sheetDescription,
  disabled,
  className,
  onPick,
  topOption,
  ...tree
}: ScopeTreePickerProps) {
  const [open, setOpen] = useState(false);

  const pick = (s: ScopeSelection) => {
    onPick(s);
    setOpen(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn(
          'inline-flex min-w-0 max-w-80 items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm transition-colors',
          'hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
      >
        {triggerIcon}
        <span className="truncate font-medium" title={triggerLabel}>
          {triggerLabel}
        </span>
        {triggerHint && (
          <span className="shrink-0 text-xs text-muted-foreground">{triggerHint}</span>
        )}
        <ChevronDown className="h-3 w-3 shrink-0 opacity-60" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-[380px] flex-col p-0 sm:max-w-[380px]">
          <SheetTitle className="sr-only">{sheetTitle}</SheetTitle>
          <SheetDescription className="sr-only">{sheetDescription}</SheetDescription>
          <ScopeTreeView
            {...tree}
            onPick={pick}
            topOption={
              topOption && {
                ...topOption,
                onSelect: () => {
                  topOption.onSelect();
                  setOpen(false);
                },
              }
            }
          />
        </SheetContent>
      </Sheet>
    </>
  );
}
